import React, { useCallback, useEffect, useRef, useState } from 'react';

// Draw, type or upload a signature. Every mode hands back a PNG data URL.
//
// Fix over the previous signing page: the typed signature was drawn during
// render onto a canvas that only exists after the first render, so "Apply"
// could send nothing. Here it's drawn in an effect onto an offscreen canvas.

// Pen colour from the theme token, so the palette stays in one place.
const inkColour = () => {
  const channels = getComputedStyle(document.documentElement).getPropertyValue('--wc-ink').trim();
  return channels ? `rgb(${channels})` : 'black';
};
const MAX_UPLOAD = 5 * 1024 * 1024;

function renderTyped(name) {
  const canvas = document.createElement('canvas');
  const W = 600;
  const H = 140;
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = inkColour();
  ctx.font = 'italic 52px "Brush Script MT", "Lucida Handwriting", "Apple Chancery", "Segoe Script", cursive';
  ctx.textBaseline = 'middle';
  ctx.fillText(name, 24, H / 2, W - 48);
  return canvas.toDataURL('image/png');
}

const TABS = [
  { key: 'draw', label: 'Draw' },
  { key: 'type', label: 'Type' },
  { key: 'upload', label: 'Upload' },
];

const SignatureModal = ({ fullName, onClose, onApply }) => {
  const [tab, setTab] = useState('draw');
  const [drawn, setDrawn] = useState(null);
  const [typedName, setTypedName] = useState(fullName || '');
  const [typed, setTyped] = useState(null);
  const [uploaded, setUploaded] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const canvasRef = useRef(null);
  const pen = useRef({ down: false, x: 0, y: 0 });

  const resetCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = inkColour();
    ctx.lineWidth = 2.2;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    setDrawn(null);
  }, []);

  useEffect(() => { if (tab === 'draw') resetCanvas(); }, [tab, resetCanvas]);
  useEffect(() => { setTyped(typedName.trim() ? renderTyped(typedName.trim()) : null); }, [typedName]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const point = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const down = (e) => {
    const p = point(e);
    pen.current = { down: true, ...p };
    canvasRef.current.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    if (!pen.current.down) return;
    const p = point(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(pen.current.x, pen.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    pen.current = { down: true, ...p };
  };
  const up = () => {
    if (!pen.current.down) return;
    pen.current.down = false;
    setDrawn(canvasRef.current.toDataURL('image/png'));
  };

  const pickFile = (file) => {
    setUploadError('');
    if (!file) return;
    if (!/^image\/(png|jpeg)$/.test(file.type)) { setUploadError('Choose a PNG or JPEG image.'); return; }
    if (file.size > MAX_UPLOAD) { setUploadError('The image must be 5 MB or smaller.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // Re-encode as PNG, scaled down so it stays well under the 1 MB limit.
        const scale = Math.min(1, 800 / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setUploaded(canvas.toDataURL('image/png'));
      };
      img.onerror = () => setUploadError('That image could not be read.');
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const current = tab === 'draw' ? drawn : tab === 'type' ? typed : uploaded;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} role="dialog" aria-modal="true" aria-label="Add your signature">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Add your signature</h3>
          <button type="button" onClick={onClose} className="text-2xl leading-none text-muted hover:text-ink" aria-label="Close">×</button>
        </div>

        <div className="mb-4 flex border-b border-line">
          {TABS.map((t) => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)}
              className={`flex-1 border-b-2 py-2 text-sm font-medium ${tab === t.key ? 'border-primary text-primary' : 'border-transparent text-muted'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'draw' && (
          <div>
            <canvas ref={canvasRef} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
              className="block h-48 w-full cursor-crosshair touch-none rounded-lg border border-line bg-white" />
            <div className="mt-2 flex items-center justify-between text-xs text-muted">
              <span>Sign with your finger, stylus or mouse.</span>
              <button type="button" onClick={resetCanvas} className="underline hover:text-ink">Clear</button>
            </div>
          </div>
        )}

        {tab === 'type' && (
          <div>
            <label className="wc-label" htmlFor="typed-signature">Your name</label>
            <input id="typed-signature" className="wc-input" value={typedName} onChange={(e) => setTypedName(e.target.value)} />
            {typed && <img src={typed} alt="Typed signature preview" className="mt-3 w-full rounded-lg border border-line" />}
          </div>
        )}

        {tab === 'upload' && (
          <div>
            <input type="file" accept="image/png,image/jpeg" onChange={(e) => pickFile(e.target.files && e.target.files[0])} className="block w-full text-sm" />
            {uploadError && <p className="mt-2 text-sm text-danger">{uploadError}</p>}
            {uploaded && <img src={uploaded} alt="Uploaded signature preview" className="mt-3 max-h-32 rounded-lg border border-line" />}
            <p className="mt-2 text-xs text-muted">A photo of your signature on white paper works well.</p>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="wc-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="wc-btn-primary" disabled={!current} onClick={() => onApply(current, tab)}>Use this signature</button>
        </div>
      </div>
    </div>
  );
};

export default SignatureModal;
